import { createContext, useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router';
import usePatientLists from '../hooks/usePatientLists';
import usePatientNotes from '../hooks/usePatientNotes';

export const PatientTabsContext = createContext({});
export const PatientTabsContextProvider = ({ children, props }) => {
  const navigate = useNavigate();
  let { uuid, listId } = useParams();

  const { data: dataObject } = usePatientLists();
  const { data: allPatientListData } = dataObject ?? [];
  const allPatientLists = allPatientListData ? allPatientListData.map((item) => item.patient_ids) : [];
  const [lastListId, setLastListId] = useState(null);

  // Load initial state from localStorage
  const initialTabs = JSON.parse(localStorage.getItem('patientTabs')) || [];
  const initialActiveTab = localStorage.getItem('activePatientTab') || null;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [activeTab, setActiveTab] = useState(initialActiveTab);
  const [openTabs, setOpenTabs] = useState(initialTabs);
  const currentTab = useMemo(
    () =>
      null || (openTabs.length && activeTab !== null) ? openTabs.filter((val) => val.uuid === activeTab)[0] : null,
    [openTabs, activeTab],
  );

  // Save to localStorage whenever tabs change
  useEffect(() => {
    localStorage.setItem('patientTabs', JSON.stringify(openTabs));
  }, [openTabs]);

  // Save active tab to localStorage
  useEffect(() => {
    if (activeTab) {
      localStorage.setItem('activePatientTab', activeTab);
    } else {
      localStorage.removeItem('activePatientTab');
    }
  }, [activeTab]);

  const openPatientTab = useCallback(
    (row) => {
      if (row && row.uuid) {
        const tabExists = openTabs.some((tab) => tab.uuid === row.uuid);
        if (!tabExists) {
          let tempTabs = [...openTabs];
          tempTabs.push(row);
          setOpenTabs(tempTabs);
          setActiveTab(row.uuid);
        } else {
          setActiveTab(row.uuid);
        }
      } else {
        //alert(JSON.stringify(row))
      }
    },
    [openTabs],
  );

  const setCorrectPage = useCallback(
    (newRowsPerPage) => {
      let topOfPageUser = page * rowsPerPage + 1;
      let newPage = Math.floor(topOfPageUser / newRowsPerPage);
      setPage(newPage);
    },
    [page, rowsPerPage],
  );

  const closePatientTab = useCallback(
    (closedUUID) => {
      setOpenTabs(openTabs.filter((val) => val.uuid != closedUUID));
      if (activeTab === closedUUID) {
        goHomeTab();
        if (openTabs.length <= 1) {
          // If closing last tab
          localStorage.removeItem('patientTabs');
          localStorage.removeItem('activePatientTab');
        }
      }
    },
    [openTabs, activeTab],
  );

  const goListTab = useCallback(
    (listId) => {
      navigate('../patients/list/' + listId, { replace: true });
      setActiveTab(null);
    },
    [openTabs, activeTab],
  );

  const goHomeTab = useCallback(() => {
    if (lastListId) {
      goListTab(lastListId);
    } else {
      navigate('../patients/', { replace: true });
      setActiveTab(null);
    }
    //    window.history.replaceState(null, window.document.title,getClientHost(code) + '/patients/');
  }, [openTabs, activeTab]);

  const {
    isLoading: isLoadingNotes,
    isRefetching: isRefetchingNotes,
    data: dataObjectNotes,
    refetch: refetchNotes,
  } = usePatientNotes(currentTab ? currentTab.id : null);
  const { data: patientNotes } = dataObjectNotes ?? [];

  return (
    <PatientTabsContext.Provider
      value={{
        actions: {
          setPage,
          setRowsPerPage,
          setLastListId,
          setActiveTab,
          openPatientTab,
          closePatientTab,
          goHomeTab,
          goListTab,
          refetchNotes,
          setCorrectPage,
        },
        state: {
          page,
          rowsPerPage,
          activeTab,
          openTabs,
          currentTab,
          uuid,
          listId,
          allPatientLists,
          patientNotes,
          isLoadingNotes,
          isRefetchingNotes,
        },
      }}
      {...props}
    >
      {children}
    </PatientTabsContext.Provider>
  );
};
